import type { StatsigLocaleDynamicConfig } from '@src/config/localisation/statsig-locale-config.types'

/**
 * National-number lengths used when the locale config carries no
 * `phoneNumberLength`. Matches the legacy US/CA rule (exactly 10 national
 * digits) so callers without a resolved locale config keep their behaviour.
 */
export const DEFAULT_PHONE_NUMBER_LENGTHS = [10]

/**
 * Strip the locale's dial code prefix from a stored phone number so the
 * remaining national digits can be pre-filled into a national-only input
 * (e.g. the address edit form) or fed into a display formatter.
 *
 * Returns the phone unchanged when the prefix doesn't match — legacy
 * data saved without any country code is left alone. The backend
 * (customer-services) normalises every write to E.164 via libphonenumber,
 * so the canonical input here is always `+<dial-code><national-digits>`.
 */
export function stripPhoneCountryCode(
  phoneNumber: string | null | undefined,
  phoneCountryCode: string | null | undefined,
): string | null | undefined {
  if (!phoneNumber) return phoneNumber
  if (phoneCountryCode && phoneNumber.startsWith(phoneCountryCode)) {
    return phoneNumber.slice(phoneCountryCode.length)
  }
  return phoneNumber
}

/**
 * Format a stored E.164-ish phone number for display.
 *
 * Delegates to `stripPhoneCountryCode` so the "what counts as a country
 * code" rule lives in exactly one place. After stripping, every non-digit
 * is dropped (`replace(/\D/g, '')`).
 *
 * The (NNN) NNN-NNNN mask is US-only. It is applied only when the locale's
 * dial code is `+1` and the cleaned national number is exactly 10 digits.
 * Other locales (e.g. UK `+44`) display the cleaned national digits as-is,
 * since the parenthesised mask is a North American convention and would be
 * misleading elsewhere.
 */
export function formatStoredPhoneNumber(
  phoneNumber: string | null | undefined,
  phoneCountryCode: string | null | undefined,
): string | null | undefined {
  if (!phoneNumber) return phoneNumber
  const cleaned = (stripPhoneCountryCode(phoneNumber, phoneCountryCode) ?? '').replace(/\D/g, '')
  if (phoneCountryCode === '+1' && cleaned.length === 10) {
    // eslint-disable-next-line i18next/no-literal-string
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`
  }
  return cleaned
}

/**
 * Reduce any phone input to its national digits: strip the locale dial code
 * (via {@link stripPhoneCountryCode}) and then drop every non-digit. This is the
 * canonical form validation runs against, so both `+447911123456` and a
 * nationally-typed `07911 123456` collapse to the same string for a UK locale.
 */
export function getNationalPhoneDigits(
  phoneNumber: string | null | undefined,
  phoneCountryCode: string | null | undefined,
): string {
  return (stripPhoneCountryCode(phoneNumber, phoneCountryCode) ?? '').replace(/\D/g, '')
}

/**
 * Resolve the valid national-number lengths for a locale, falling back to
 * {@link DEFAULT_PHONE_NUMBER_LENGTHS} (US/CA 10-digit) when the config is
 * missing or empty.
 */
export function getAllowedPhoneLengths(localeConfig?: StatsigLocaleDynamicConfig | null): number[] {
  const lengths = localeConfig?.phoneNumberLength
  return lengths && lengths.length > 0 ? lengths : DEFAULT_PHONE_NUMBER_LENGTHS
}

/**
 * Validate a phone number against the locale's `phoneNumberLength` config.
 *
 * Mirrors `validateZipCode` (config-driven, no country branching) and the same
 * length-based rule web-checkout uses: the dial code (`phoneCountryCode`) and
 * non-digits are stripped, then the national-digit count must be one of the
 * locale's allowed lengths (US/CA → [10], UK → [10, 11]).
 */
export function validatePhoneForLocale(
  phoneNumber: string | null | undefined,
  localeConfig?: StatsigLocaleDynamicConfig | null,
): boolean {
  if (!phoneNumber) return false
  const national = getNationalPhoneDigits(phoneNumber, localeConfig?.phoneCountryCode)
  if (!national) return false
  return getAllowedPhoneLengths(localeConfig).includes(national.length)
}

/**
 * Build the national-digit RegExp for a locale, for consumers (e.g. react-hook-form
 * `pattern`) that validate the raw input value rather than calling
 * {@link validatePhoneForLocale}. Matches a digit string whose length is one of
 * the locale's allowed lengths (e.g. [10, 11] → /^(?:\d{10}|\d{11})$/).
 */
export function getPhoneNumberPattern(localeConfig?: StatsigLocaleDynamicConfig | null): RegExp {
  const alternatives = getAllowedPhoneLengths(localeConfig)
    // eslint-disable-next-line i18next/no-literal-string
    .map(len => `\\d{${len}}`)
    .join('|')
  // eslint-disable-next-line i18next/no-literal-string
  return new RegExp(`^(?:${alternatives})$`)
}
